<template>
    <div class="vp-root" :style="rootStyle">
        <VideoTrafficBlocked v-if="showBlocked" />

        <template v-else-if="isUnavailable">
            <slot name="unavailable">
                <VideoUnavailable :title="t('Video unavailable')" :body="unavailableBody" />
            </slot>
        </template>

        <media-controller v-else ref="controllerEl" class="vp-controller" autohide="3" :lang="locale" :defaultduration="activeDuration || null">
            <!-- preload="none" (IAB): no duration until play, so defaultduration lays out the time bar and chapter segments from the server's. -->
            <video v-if="videoTrack" ref="videoEl" :slot="videoSlot" playsinline preload="none"></video>
            <audio v-if="audioTrack" ref="audioEl" :slot="audioSlot" preload="none"></audio>

            <VideoAudioBackdrop v-if="mode === 'audio'" slot="centered-chrome" :artwork="activeTrack.artworkUrl" :caption="audioCaption" />

            <VideoTopBar
                v-if="started"
                slot="top-chrome"
                :show-label="label"
                :title="activeTrack.title"
                :branding-visible="branding"
                :transcript-available="activeTrack.transcripts.length > 0"
                :transcript-open="transcriptOpen"
                :switch-visible="canSwitch"
                :mode="mode"
                @switch-mode="onSwitchMode"
                @toggle-transcript="transcriptOpen = !transcriptOpen"
            >
                <template #actions>
                    <slot name="top-bar-actions" />
                </template>
            </VideoTopBar>

            <slot v-if="!started" name="poster" :play="onPosterPlay" :track="activeTrack" :chapter-count="chapters.length" :has-transcript="activeTrack.transcripts.length > 0" />

            <VideoChapterMenu
                slot="centered-chrome"
                :open="chaptersOpen"
                :chapters="chapters"
                :active-index="activeChapterIndex"
                @seek="onChapterSeek"
                @close="chaptersOpen = false"
            />

            <VideoTranscriptPanel
                slot="centered-chrome"
                :open="transcriptOpen"
                :loading="transcriptsLoading"
                :cues="transcriptCues"
                :active-index="activeTranscriptIndex"
                @seek="seek"
                @close="transcriptOpen = false"
            />

            <slot v-if="ended" name="end-screen" :replay="replay" />

            <VideoCtaBar :cta="activeCta" @click="onCtaClick" @dismiss="dismissCta" />

            <VideoControlBar
                :chapter-label="chapterLabel"
                :has-chapters="chapters.length > 0"
                :chapters-open="chaptersOpen"
                :has-thumbnails="hasThumbnails && mode === 'video'"
                :has-subtitles="hasSubtitles"
                :levels="levels"
                :quality-visible="qualityVisible && mode === 'video'"
                :selected-level="selectedLevel"
                :video-controls="mode === 'video'"
                @toggle-chapters="chaptersOpen = !chaptersOpen"
                @select-quality="selectQuality"
            />
        </media-controller>
    </div>
</template>

<script setup>
import "media-chrome/media-controller.js"
// media-controller opens this dialog on "?" but does not define it itself.
import "media-chrome/media-keyboard-shortcuts-dialog.js"
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue"
import { resolveActiveCta } from "../Player/helpers.js"
import VideoAudioBackdrop from "./Components/VideoAudioBackdrop.vue"
import VideoChapterMenu from "./Components/VideoChapterMenu.vue"
import VideoControlBar from "./Components/VideoControlBar.vue"
import VideoCtaBar from "./Components/VideoCtaBar.vue"
import VideoTopBar from "./Components/VideoTopBar.vue"
import VideoTrafficBlocked from "./Components/VideoTrafficBlocked.vue"
import VideoTranscriptPanel from "./Components/VideoTranscriptPanel.vue"
import VideoUnavailable from "./Components/VideoUnavailable.vue"
import { seekWhenReady } from "./mediaSwitch.js"
import { DESIGN_PALETTE } from "./palette.js"
import { intendedPosition } from "./programmaticSeek.js"
import { createShellWiring } from "./shellWiring.js"
import { applyTextTracks, measureImages, removeTextTracks } from "./textTracks.js"
import { activeIndex } from "./timelineView.js"
import { trackDuration, warnOnInvalidTracks } from "./trackContract.js"
import { chapterCuesFor, thumbnailCuesFor, trackSpecsFor, transcriptCuesFor } from "./trackOverlays.js"
import { createTranscriptStore, defaultTranscriptLoader } from "./transcriptStore.js"
import { useCoreI18n } from "./useCoreI18n.js"
import { useVideoPlayback } from "./useVideoPlayback.js"

/**
 * SC-1323 §4.2: the one player of the podcast embed and the learning portal. It plays one or two
 * normalized tracks (PlayerCore/trackContract.js) and knows nothing about where they come from;
 * access and statistics are the page's (the `player-event` stream).
 */
const props = defineProps({
    tracks: { type: Array, required: true },
    sameTimeline: { type: Boolean, default: false },
    initialMode: { type: String, default: null },
    resume: { type: Object, default: () => ({}) },
    locale: { type: String, default: "en" },
    theme: { type: Object, default: () => ({}) },
    trafficBlocked: { type: Boolean, default: false },
    unavailable: { type: Boolean, default: false },
    label: { type: String, default: "" },
    branding: { type: Boolean, default: false },
    mediaSession: { type: Object, default: null },
    loadTranscript: { type: Function, default: defaultTranscriptLoader },
    failOnFatal: { type: Boolean, default: false },
})

const emit = defineEmits(["player-event", "player-error", "ready"])

const t = useCoreI18n()
const unavailableBody = t("This video can't be played right now. Please try again later.")

warnOnInvalidTracks(props.tracks)

// Tracks are fixed for the page's life, so these are plain constants: Vue never re-renders the
// slot attributes the media switch moves imperatively.
const videoTrack = props.tracks.find((track) => track.kind === "video") ?? null
const audioTrack = props.tracks.find((track) => track.kind === "audio") ?? null
const initialTrack = (props.initialMode === "audio" ? audioTrack : videoTrack) ?? videoTrack ?? audioTrack
const videoSlot = initialTrack?.kind === "video" ? "media" : "standby"
const audioSlot = initialTrack?.kind === "audio" ? "media" : "standby"
const canSwitch = videoTrack !== null && audioTrack !== null

// Theme tokens live on the shell root, not on :root, so nothing leaks into other pages.
const rootStyle = computed(() => ({ ...DESIGN_PALETTE, ...props.theme }))

const controllerEl = ref(null)
const videoEl = ref(null)
const audioEl = ref(null)
const failed = ref(false)

function onFatal() {
    failed.value = true
    emit("player-error", "media_failed")
}

const { levels, currentLevel, isNative, setLevel, attach, destroy, stopLoad, startLoad, blocked: playbackBlocked } = useVideoPlayback({ onFatalError: props.failOnFatal ? onFatal : null })

// Server-rendered block (quota already reached at page load) OR a 403 hit mid-stream while
// watching. Either one swaps in the "video unavailable" screen.
const showBlocked = computed(() => props.trafficBlocked || playbackBlocked.value)
const isUnavailable = computed(() => props.unavailable || failed.value || initialTrack === null)

// Safari native HLS has no programmatic level switching.
const qualityVisible = computed(() => !isNative.value)

// The picked rung lives here, not in the menu: the menu remounts after an audio round trip
// while hls.js stays pinned to the rung.
const selectedLevel = ref(-1)

function selectQuality(index) {
    selectedLevel.value = index
    setLevel(index)
}

// "video" plays the video track's timeline, "audio" the audio track's; overlays read the active
// element's own time and the active track's own overlays.
const mode = ref(initialTrack?.kind ?? "video")
const activeTrack = computed(() => (mode.value === "audio" ? audioTrack : videoTrack) ?? initialTrack)
const elementFor = (track) => (track?.kind === "audio" ? audioEl.value : videoEl.value)
const activeMedia = () => elementFor(activeTrack.value)
const activeDuration = computed(() => (activeTrack.value ? trackDuration(activeTrack.value) : 0))
const currentTime = ref(0)
const chaptersOpen = ref(false)
const transcriptOpen = ref(false)
const started = ref(false)
const ended = ref(false)
const audioCaption = ref("")
const thumbnailSizes = shallowRef(new Map())

const transcripts = createTranscriptStore({ load: props.loadTranscript })
const transcriptTick = ref(0)
const transcriptsLoading = ref(false)
const activeTranscriptTexts = computed(() => {
    transcriptTick.value

    return activeTrack.value ? transcripts.textsFor(activeTrack.value) : []
})
const hasSubtitles = computed(() => activeTranscriptTexts.value.length > 0)

async function loadTranscripts() {
    if (!props.tracks.some((track) => track.transcripts.length > 0)) {
        return
    }

    transcriptsLoading.value = true
    await transcripts.loadFor(props.tracks)
    transcriptsLoading.value = false
    transcriptTick.value++
}

const chapters = computed(() => chapterCuesFor(activeTrack.value))
const activeChapterIndex = computed(() => activeIndex(chapters.value, currentTime.value))
const chapterLabel = computed(() => {
    const index = activeChapterIndex.value

    return index < 0 ? null : `${t("Chapter {n}", { n: index + 1 })} · ${chapters.value[index].text}`
})
const videoChapterCues = computed(() => chapterCuesFor(videoTrack))
const videoThumbnailCues = computed(() => (videoTrack ? thumbnailCuesFor(videoChapterCues.value, videoTrack.artworkUrl, thumbnailSizes.value) : []))
const hasThumbnails = computed(() => videoThumbnailCues.value.length > 0)

function videoTrackSpecs() {
    return trackSpecsFor({
        track: videoTrack,
        chapterCues: videoChapterCues.value,
        thumbnailCues: videoThumbnailCues.value,
        transcripts: transcripts.textsFor(videoTrack),
        chaptersLabel: t("Chapters"),
        locale: props.locale,
    })
}

function audioTrackSpecs() {
    return trackSpecsFor({ track: audioTrack, chapterCues: chapterCuesFor(audioTrack), transcripts: transcripts.textsFor(audioTrack), chaptersLabel: t("Chapters"), locale: props.locale })
}

// The audio element gets its tracks once its source is set (as the podcast switch always did).
let audioTracksApplied = false

function applyAudioTracks() {
    if (audioEl.value && audioTrack) {
        applyTextTracks(audioEl.value, audioTrackSpecs())
        audioTracksApplied = true
    }
}

watch([videoChapterCues, videoThumbnailCues, transcriptTick], () => {
    if (videoEl.value && videoTrack) {
        applyTextTracks(videoEl.value, videoTrackSpecs())
    }
})

watch(transcriptTick, () => {
    if (audioTracksApplied) {
        applyAudioTracks()
    }
})

async function loadThumbnailSizes() {
    if (!videoChapterCues.value.some((cue) => cue.image)) {
        return
    }

    thumbnailSizes.value = await measureImages(videoChapterCues.value.map((cue) => cue.image || videoTrack.artworkUrl))
}

// <audio> has no caption surface: show the cue of the subtitle track media-chrome's captions
// button switched to "showing".
function syncAudioCaption() {
    const tracks = Array.from(audioEl.value?.textTracks ?? [])
    const showing = tracks.find((track) => (track.kind === "subtitles" || track.kind === "captions") && track.mode === "showing")
    const cue = showing?.activeCues?.[0]

    audioCaption.value = cue ? (cue.getCueAsHTML?.().textContent ?? cue.text) : ""
}

function updateMediaSession() {
    if (!props.mediaSession || !("mediaSession" in navigator) || typeof MediaMetadata === "undefined" || !activeTrack.value) {
        return
    }

    const image = activeTrack.value.artworkUrl

    navigator.mediaSession.metadata = new MediaMetadata({
        title: activeTrack.value.title,
        artist: props.mediaSession.artist,
        album: props.mediaSession.album,
        artwork: image ? [{ src: image, sizes: "512x512" }] : [],
    })
}

// The CTA list is the active track's, already on its timeline. A dismissed CTA stays dismissed.
const dismissedCtaIds = ref(new Set())
const activeCta = computed(() => {
    const cta = resolveActiveCta(currentTime.value, activeTrack.value?.ctas ?? [])

    return cta && !dismissedCtaIds.value.has(cta.id) ? cta : null
})

function dismissCta(cta) {
    dismissedCtaIds.value = new Set([...dismissedCtaIds.value, cta.id])
}

// The panel shows the first transcript, shifted onto the active track's timeline.
const transcriptCues = computed(() => {
    const first = activeTranscriptTexts.value[0]

    return first ? transcriptCuesFor(first.text, activeTrack.value) : []
})
const activeTranscriptIndex = computed(() => activeIndex(transcriptCues.value, currentTime.value))

// The chapter menu and the transcript panel share the right-hand side.
watch(transcriptOpen, (open) => {
    if (open) {
        chaptersOpen.value = false
    }
})
watch(chaptersOpen, (open) => {
    if (open) {
        transcriptOpen.value = false
    }
})

function resumeFor(trackId) {
    const seconds = Number(props.resume?.[trackId])

    return Number.isFinite(seconds) && seconds > 0 ? seconds : 0
}

// The statistics and switching wiring lives in shellWiring.js, so the golden harness runs this same
// code (tests/js/golden/harness.js shellHarness).
const wiring = createShellWiring({
    tracks: props.tracks,
    sameTimeline: props.sameTimeline,
    initialMode: initialTrack?.kind ?? "video",
    emit: (event) => emit("player-event", event),
    isFullscreen: () => !!document.fullscreenElement,
    levels: () => levels.value,
    currentLevel: () => currentLevel.value,
    resumeFor,
})

function syncTime(event) {
    if (event.target === activeMedia()) {
        currentTime.value = event.target.currentTime || 0
    }
}

function onMediaEnded(event) {
    if (event.target !== activeMedia()) {
        return
    }

    ended.value = true
    chaptersOpen.value = false
    transcriptOpen.value = false
}

function onMediaPlay(event) {
    if (event.target === activeMedia()) {
        started.value = true
        ended.value = false
    }
}

function onPosterPlay() {
    activeMedia()
        ?.play()
        .catch(() => {})
}

// Seeking away from the end (control bar stays usable under the screen) dismisses it.
function onMediaSeeking(event) {
    const el = event.target

    if (el === activeMedia() && ended.value && el.currentTime < el.duration - 0.5) {
        ended.value = false
    }
}

function onElementError(event) {
    if (event.target !== activeMedia()) {
        return
    }

    emit("player-error", "media_failed")

    if (props.failOnFatal) {
        failed.value = true
    }
}

function replay() {
    const el = activeMedia()

    if (!el) {
        return
    }

    ended.value = false
    el.currentTime = 0
    el.play().catch(() => {})
}

// Page-state listeners for each media element; removed again on unmount.
const watchedMedia = new Map()

function watchMediaElement(el) {
    if (watchedMedia.has(el)) {
        return
    }

    const handlers = { timeupdate: syncTime, seeked: syncTime, ended: onMediaEnded, play: onMediaPlay, seeking: onMediaSeeking, error: onElementError }

    for (const [type, handler] of Object.entries(handlers)) {
        el.addEventListener(type, handler)
    }

    watchedMedia.set(el, handlers)
}

function unwatchMediaElements() {
    for (const [el, handlers] of watchedMedia) {
        for (const [type, handler] of Object.entries(handlers)) {
            el.removeEventListener(type, handler)
        }
    }

    watchedMedia.clear()
}

function seek(position, { programmatic = false } = {}) {
    const el = activeMedia()

    if (!el || !Number.isFinite(position)) {
        return
    }

    const target = Math.max(0, position)

    if (programmatic) {
        seekWhenReady(el, target)
    } else {
        el.currentTime = target
    }
}

function onChapterSeek(seconds) {
    seek(seconds)
    chaptersOpen.value = false
}

function onCtaClick(cta) {
    wiring.ctaClick(cta)
}

/**
 * Runs inside the toggle's click handler. Everything up to the switcher's play() is synchronous
 * (no await, no nextTick), so iOS keeps the user gesture: wiring.switchMode() switches and reports
 * the mode_switch, the shell then updates what it shows. The pause of the side left and the play of
 * the side started come from the elements' own events.
 */
function switchMode(next, { play = true, report = true } = {}) {
    const result = wiring.switchMode(next, { play, report })

    if (result === null) {
        return
    }

    mode.value = next

    // The fullscreen and picture-in-picture buttons are gone in audio mode, so leave both with them.
    if (next === "audio") {
        if (document.fullscreenElement) {
            document.exitFullscreen?.()?.catch?.(() => {})
        }

        if (document.pictureInPictureElement) {
            document.exitPictureInPicture?.()?.catch?.(() => {})
        }
    }

    // intendedPosition: right after the first switch iOS still reads 0 on the target until its
    // metadata lands and the seek is re-applied.
    const active = elementFor(result.toTrack)
    currentTime.value = active ? intendedPosition(active) : 0
    audioCaption.value = ""

    if (next === "audio" && !audioTracksApplied) {
        applyAudioTracks()
    }

    // Separate items: the end screen of one item says nothing about the other.
    if (!props.sameTimeline) {
        ended.value = false
    }

    updateMediaSession()
    result.playing.catch(() => {})
}

function onSwitchMode(next) {
    switchMode(next)
}

async function startVideo() {
    const url = videoTrack?.media?.hls?.masterUrl
    const el = videoEl.value

    if (!url || !el) {
        return
    }

    // Tracks go on before hls.js attaches so the chapters show at once...
    applyTextTracks(el, videoTrackSpecs())

    // A fresh hls.js instance starts on auto.
    selectedLevel.value = -1
    await attach(el, url)

    // ...and are rebuilt after it: hls.js empties every text track's cues when it attaches.
    // Nothing is fetched; the VTTs are local blobs.
    applyTextTracks(el, videoTrackSpecs(), { rebuild: true })
}

// hls.js LEVEL_SWITCHED → currentLevel changes; wiring.qualityChanged reports real changes only.
watch(currentLevel, (newLevel, oldLevel) => wiring.qualityChanged(newLevel, oldLevel))

watch(playbackBlocked, (blocked) => {
    if (blocked) {
        emit("player-error", "traffic_blocked")
    }
})

// document.fullscreenchange fires for every fullscreen transition (button, hotkey, F11, PiP exit).
function onFullscreenChange() {
    wiring.fullscreenChanged()
}

// Body reset: the iframe / browser must not draw its own scrollbars while the shell is mounted.
const previousBodyStyle = {}

onMounted(async () => {
    for (const key of ["margin", "padding", "height", "overflow"]) {
        previousBodyStyle[key] = document.body.style[key]
    }

    Object.assign(document.body.style, { margin: "0", padding: "0", height: "100%", overflow: "hidden" })
    document.documentElement.style.height = "100%"
    document.documentElement.style.overflow = "hidden"
    document.addEventListener("fullscreenchange", onFullscreenChange)

    if (showBlocked.value || isUnavailable.value) {
        emit("player-error", props.trafficBlocked ? "traffic_blocked" : "unavailable")

        return
    }

    for (const el of [videoEl.value, audioEl.value]) {
        if (el) {
            watchMediaElement(el)
        }
    }

    // Binds playerEvents to both elements and, with two tracks, creates the track switcher.
    wiring.attach({ controller: controllerEl.value, video: videoEl.value, audio: audioEl.value, playback: { stopLoad, startLoad } })

    audioEl.value?.addEventListener("timeupdate", syncAudioCaption)
    updateMediaSession()

    if (!canSwitch && initialTrack.kind === "audio") {
        audioEl.value.src = audioTrack.media.audio.url
    }

    if (initialTrack.kind === "audio") {
        applyAudioTracks()
    }

    loadTranscripts()
    loadThumbnailSizes()
    await startVideo()

    const resumeAt = resumeFor(initialTrack.id)

    if (resumeAt > 0) {
        seekWhenReady(activeMedia(), resumeAt)
    }

    emit("ready")
})

onBeforeUnmount(() => {
    destroy()
    wiring.detach()

    for (const el of [videoEl.value, audioEl.value]) {
        if (el) {
            removeTextTracks(el)
        }
    }

    if (audioEl.value) {
        audioEl.value.removeEventListener("timeupdate", syncAudioCaption)
        audioEl.value.pause()
    }

    if (props.mediaSession && "mediaSession" in navigator) {
        navigator.mediaSession.metadata = null
    }

    unwatchMediaElements()
    document.removeEventListener("fullscreenchange", onFullscreenChange)

    for (const [key, value] of Object.entries(previousBodyStyle)) {
        document.body.style[key] = value
    }

    document.documentElement.style.height = ""
    document.documentElement.style.overflow = ""
})

defineExpose({
    seek,
    play: () =>
        activeMedia()
            ?.play()
            .catch(() => {}),
    pause: () => activeMedia()?.pause(),
    setMode: (next, options = {}) => switchMode(next, options),
    activeTrack: () => activeTrack.value,
    mode: () => mode.value,
    audioElement: () => audioEl.value,
})
</script>

<style>
.vp-root {
    position: fixed;
    inset: 0;
    overflow: hidden;
    background: var(--vp-ink);
    color: var(--vp-paper);
    font-family: "Inter", -apple-system, system-ui, "Segoe UI", sans-serif;
}

.vp-controller {
    display: block;
    width: 100%;
    height: 100%;
    line-height: 1.4;
    --media-background-color: var(--vp-ink);
    --media-primary-color: var(--vp-paper);
    --media-secondary-color: transparent;
    --media-text-color: var(--vp-paper);
    --media-icon-color: var(--vp-paper);
    --media-control-background: transparent;
    --media-control-hover-background: rgba(251, 249, 245, 0.08);
    --media-font-family: "Inter", system-ui, sans-serif;
    --media-focus-box-shadow: inset 0 0 0 2px var(--vp-accent);
}

/* Preflight's `video { height: auto }` beats media-chrome's shadow
   ::slotted([slot=media]) sizing, which let the picture grow to its 16:9
   height and spill out of the player. Fill the controller and letterbox. */
.vp-controller > video[slot="media"] {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.vp-controller > video[slot="media"]::cue {
    font-family: "Inter", system-ui, sans-serif;
    line-height: 1.35;
    color: var(--vp-paper);
    background-color: rgba(21, 18, 13, 0.78);
}

.vp-label {
    font-family: "JetBrains Mono", ui-monospace, monospace;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(207, 200, 188, 0.86);
}

.vp-heading {
    font-family: "Geist", system-ui, sans-serif;
    font-weight: 700;
    letter-spacing: -0.018em;
    color: var(--vp-paper);
}

.vp-mono {
    font-family: "JetBrains Mono", ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
}

.vp-panel {
    background: var(--vp-panel);
    border: 1px solid rgba(251, 249, 245, 0.16);
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(21, 18, 13, 0.4);
}

.vp-icon-button {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: var(--vp-paper);
    background: transparent;
    cursor: pointer;
}

.vp-icon-button:hover {
    background: rgba(251, 249, 245, 0.08);
}

.vp-icon-button[aria-pressed="true"],
.vp-icon-button[aria-expanded="true"] {
    background: rgba(251, 249, 245, 0.14);
}

.vp-icon-button:focus-visible,
.vp-outline-button:focus-visible {
    outline: 2px solid var(--vp-accent);
    outline-offset: 2px;
}

.vp-outline-button {
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(251, 249, 245, 0.24);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--vp-paper);
    text-decoration: none;
    background: transparent;
    cursor: pointer;
}

.vp-outline-button--small {
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
}

.vp-spacer {
    flex-grow: 1;
}

.vp-dot {
    opacity: 0.5;
}

.vp-message {
    height: 100%;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    text-align: center;
}

.vp-message-title {
    margin: 0;
    font-size: 22px;
}

.vp-message-body {
    max-width: 420px;
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: var(--vp-muted);
}
</style>
